import React, { useMemo, useState } from "react";
import { ChevronLeft, ChevronRight, CheckCircle2, Clock3, XCircle, CalendarDays } from "lucide-react";

const DE_MONTHS = ["Januar","Februar","März","April","Mai","Juni","Juli","August","September","Oktober","November","Dezember"];
const DE_WD = ["Mo","Di","Mi","Do","Fr","Sa","So"];

/**
 * Simple monthly calendar showing a client's/participant's appointments.
 * Green cell = signed, blue = accepted, amber = proposed, red = rejected.
 * Click a day → shows details for that day's appointments below.
 */
export default function AvgsClientCalendar({ appointments = [] }) {
  const [cursor, setCursor] = useState(() => {
    const d = new Date();
    return { y: d.getFullYear(), m: d.getMonth() };
  });
  const [selectedDay, setSelectedDay] = useState(null);

  const daysInMonth = new Date(cursor.y, cursor.m + 1, 0).getDate();
  const firstDayIdx = (new Date(cursor.y, cursor.m, 1).getDay() + 6) % 7; // Mon = 0
  const monthLabel = `${DE_MONTHS[cursor.m]} ${cursor.y}`;

  // group appointments by ISO date
  const byDate = useMemo(() => {
    const m = {};
    for (const a of appointments) {
      const d = (a.date || "").slice(0, 10);
      if (!d) continue;
      (m[d] = m[d] || []).push(a);
    }
    return m;
  }, [appointments]);

  const cells = [];
  for (let i = 0; i < firstDayIdx; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) {
    const iso = `${cursor.y}-${String(cursor.m + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
    cells.push({ day: d, iso, appts: byDate[iso] || [] });
  }

  const todayIso = new Date().toISOString().slice(0, 10);
  const selectedAppts = selectedDay ? (byDate[selectedDay] || []) : [];

  const prev = () => setCursor(({ y, m }) => (m === 0 ? { y: y - 1, m: 11 } : { y, m: m - 1 }));
  const next = () => setCursor(({ y, m }) => (m === 11 ? { y: y + 1, m: 0 } : { y, m: m + 1 }));
  const today = () => {
    const d = new Date();
    setCursor({ y: d.getFullYear(), m: d.getMonth() });
    setSelectedDay(todayIso);
  };

  return (
    <div className="border border-slate-200 bg-white" data-testid="avgs-client-calendar">
      <div className="px-5 py-3 border-b border-slate-200 flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <CalendarDays size={14} strokeWidth={1.5} className="text-slate-500" />
          <h3 className="text-sm font-medium text-slate-900">{monthLabel}</h3>
        </div>
        <div className="flex items-center gap-1">
          <button onClick={prev} className="p-1.5 border border-slate-200 hover:bg-slate-100" data-testid="cal-prev">
            <ChevronLeft size={14} strokeWidth={1.5} />
          </button>
          <button onClick={today} className="px-2 py-1 text-[11px] font-medium border border-slate-200 hover:bg-slate-100" data-testid="cal-today">
            Heute
          </button>
          <button onClick={next} className="p-1.5 border border-slate-200 hover:bg-slate-100" data-testid="cal-next">
            <ChevronRight size={14} strokeWidth={1.5} />
          </button>
        </div>
      </div>

      <div className="grid grid-cols-7 text-[10px] font-bold uppercase tracking-wider text-slate-500 bg-slate-50">
        {DE_WD.map((d) => <div key={d} className="px-2 py-1.5 text-center border-r border-slate-100 last:border-r-0">{d}</div>)}
      </div>

      <div className="grid grid-cols-7">
        {cells.map((cell, i) => {
          if (!cell) return <div key={i} className="min-h-[70px] border-r border-b border-slate-100 bg-slate-50/50" />;
          const isToday = cell.iso === todayIso;
          const isSelected = cell.iso === selectedDay;
          const hasAppts = cell.appts.length > 0;
          return (
            <button key={cell.iso}
              onClick={() => setSelectedDay(cell.iso)}
              className={`min-h-[70px] p-1.5 border-r border-b border-slate-100 last:border-r-0 text-left transition-colors ${isSelected ? "bg-slate-900 text-white" : hasAppts ? "bg-white hover:bg-slate-50" : "bg-white hover:bg-slate-50"} ${isToday && !isSelected ? "ring-2 ring-[#00CCC9] ring-inset" : ""}`}
              data-testid={`cal-day-${cell.iso}`}>
              <div className={`text-xs font-medium ${isSelected ? "text-white" : "text-slate-900"}`}>{cell.day}</div>
              {hasAppts && (
                <div className="mt-1 space-y-0.5">
                  {cell.appts.slice(0, 2).map((a) => (
                    <div key={a.appointment_id}
                      className={`text-[9px] px-1 py-0.5 truncate ${statusChipCls(a.status, isSelected)}`}
                      title={`${a.time_from || ""}–${a.time_to || ""} · ${a.topic || "Termin"}`}>
                      {a.time_from ? a.time_from.slice(0, 5) : "•"} {a.topic || "Termin"}
                    </div>
                  ))}
                  {cell.appts.length > 2 && (
                    <div className={`text-[9px] ${isSelected ? "text-white/70" : "text-slate-500"}`}>+{cell.appts.length - 2}</div>
                  )}
                </div>
              )}
            </button>
          );
        })}
      </div>

      {selectedDay && (
        <div className="border-t border-slate-200 p-4 bg-slate-50">
          <div className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2">
            {new Date(selectedDay + "T00:00:00").toLocaleDateString("de-DE", { weekday: "long", day: "2-digit", month: "long", year: "numeric" })}
          </div>
          {selectedAppts.length === 0 ? (
            <div className="text-xs text-slate-500 italic">Keine Termine an diesem Tag.</div>
          ) : (
            <div className="space-y-2">
              {selectedAppts.map((a) => (
                <div key={a.appointment_id} className="bg-white border border-slate-200 p-3 text-xs">
                  <div className="flex items-center gap-2 mb-1">
                    {statusIcon(a.status)}
                    <span className="font-medium text-slate-900">{a.topic || "Coaching-Termin"}</span>
                  </div>
                  <div className="font-mono text-slate-600">
                    {a.time_from || "?"}–{a.time_to || "?"} · {a.planned_ue || 0} UE · {a.mode === "online" ? "Remote" : "Präsenz"}
                  </div>
                  {a.coach_name && <div className="text-slate-500 mt-1">Coach: {a.coach_name}</div>}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function statusChipCls(status, isSelected) {
  if (isSelected) return "bg-white/20 text-white";
  return {
    signed:    "bg-emerald-100 text-emerald-900",
    completed: "bg-slate-200 text-slate-900",
    accepted:  "bg-blue-100 text-blue-900",
    proposed:  "bg-amber-100 text-amber-900",
    rejected:  "bg-red-100 text-red-900",
  }[status] || "bg-slate-100 text-slate-700";
}

function statusIcon(status) {
  if (status === "signed") return <CheckCircle2 size={12} strokeWidth={1.5} className="text-emerald-600" />;
  if (status === "rejected") return <XCircle size={12} strokeWidth={1.5} className="text-red-600" />;
  return <Clock3 size={12} strokeWidth={1.5} className="text-slate-500" />;
}
